<template>
  <tr class="requisites-group">
    <td colspan="9">
      <div class="requisites-group__head">
        <button type="button"
                class="requisites-group__chevron"
                :class="{'requisites-group__chevron--open': expanded}"
                :title="expanded ? $t('requisites.group_collapse') : $t('requisites.group_expand')"
                @click="$emit('toggleExpand')">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 8L10 12L14 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>

        <input type="checkbox"
               class="form-check-input flex-shrink-0"
               :checked="isAllSelected"
               :indeterminate="isPartiallySelected"
               :title="$t('requisites.group_select_all')"
               @change="$emit('select', $event.target.checked)">

        <div class="requisites-group__icons" @click="$emit('toggleExpand')">
          <PayMethodIcon v-for="method in visibleMethods" :key="method" icon-class="pay-method-icon-xl" :method="method"></PayMethodIcon>
          <span v-if="hiddenMethodsCount > 0" class="requisites-group__more">+{{ hiddenMethodsCount }}</span>
        </div>

        <div class="requisites-group__main" @click="$emit('toggleExpand')">
          <div class="card-info__title">
            <span><strong>{{ currencies.join(', ') }}</strong> {{ paymentOptions.join(', ') }}</span>
          </div>
          <div class="card-info__number d-flex align-items-start gap-1 flex-wrap">
            <span>{{ phone }}</span>
            <copy-button :value="phone" class="flex-shrink-0 align-self-center" @click.stop></copy-button>
          </div>
          <div class="card-info__name">
            {{ holderName }}
          </div>
        </div>

        <span class="requisites-group__count">
          {{ helper.trans('requisites.group_active_count', {active: activeCount, total: group.items.length}) }}
        </span>
      </div>
    </td>
  </tr>
</template>
<script lang="ts">
import {computed, defineComponent, PropType} from 'vue'
import {$t, helper} from "@/service/CommonHelper";
import RequisitesPhoneGroupDTO from "@/models/RequisitesPhoneGroupDTO";
import PayMethodIcon from "@/components/PayMethodIcon.vue";
import CopyButton from "@/apps/public/components/CopyButton.vue";

const MAX_VISIBLE_METHODS = 4

export default defineComponent({
  methods: {$t},
  components: {PayMethodIcon, CopyButton},
  props: {
    group: {type: Object as PropType<RequisitesPhoneGroupDTO>, required: true},
    expanded: {type: Boolean, default: false},
    selectedCount: {type: Number, default: 0},
  },
  emits: ['toggleExpand', 'select'],
  setup(props) {
    const phone = computed(() => props.group.items[0].requisites)
    const holderName = computed(() => props.group.items[0].holderName)
    const activeCount = computed(() => props.group.items.filter(item => item.isActive).length)
    const isAllSelected = computed(() => props.selectedCount > 0 && props.selectedCount === props.group.items.length)
    const isPartiallySelected = computed(() => props.selectedCount > 0 && props.selectedCount < props.group.items.length)

    const methods = computed(() => Array.from(new Set(props.group.items.map(item => item.method))))
    const visibleMethods = computed(() => methods.value.slice(0, MAX_VISIBLE_METHODS))
    const hiddenMethodsCount = computed(() => Math.max(0, methods.value.length - MAX_VISIBLE_METHODS))

    const currencies = computed(() => Array.from(new Set(props.group.items.map(item => item.currency))))
    const paymentOptions = computed(() => Array.from(new Set(props.group.items.map(item => $t(`payment_options.${item.paymentOption}`)))))

    return {
      helper,
      phone,
      holderName,
      activeCount,
      isAllSelected,
      isPartiallySelected,
      visibleMethods,
      hiddenMethodsCount,
      currencies,
      paymentOptions,
    }
  }
})
</script>
<style scoped>
.requisites-group__head {
  display: flex;
  align-items: center;
  gap: 16px;
}
.requisites-group__chevron {
  display: flex;
  padding: 4px;
  background: none;
  border: none;
  color: var(--text-dark);
  cursor: pointer;
  transition: transform .2s ease;
}
.requisites-group__chevron--open {
  transform: rotate(180deg);
}
.requisites-group__icons {
  display: flex;
  align-items: center;
  cursor: pointer;
}
.requisites-group__icons img {
  box-sizing: content-box;
  border: 2px solid var(--background-second-background);
}
.requisites-group__icons > * + * {
  margin-left: -14px;
}
.requisites-group__more {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  box-sizing: content-box;
  border: 2px solid var(--background-second-background);
  border-radius: 50%;
  background: var(--buttons-main-default);
  color: var(--text-light);
  font-size: 12px;
  font-weight: 500;
}
.requisites-group__main {
  flex: 1;
  min-width: 0;
  cursor: pointer;
}
.requisites-group__count {
  margin-left: auto;
  white-space: nowrap;
  font-size: 14px;
  color: var(--second-text-color);
}
</style>
